import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { logEventSafe } from '../analytics/analytics';
import { PageBackLink, PageHero, PagePanel, PageShell } from './MarketingPageShell';

const REPO_OWNER = 'ToolipDev';
const REPO_NAME = 'PublicReleasesRepo';

const GetStarted = () => {
  const location = useLocation();
  const [latestExe, setLatestExe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    logEventSafe('page_view', {
      page_path: location.pathname,
      page_title: 'Get Started'
    });
  }, [location.pathname]);

  useEffect(() => {
    const fetchLatestRelease = async () => {
      try {
        const res = await fetch(`https://api.github.com/repos/${REPO_OWNER}/${REPO_NAME}/releases/latest`);
        if (!res.ok) {
          throw new Error('Failed to fetch release information.');
        }
        const data = await res.json();
        const exeAsset = data.assets.find((asset) => asset.name.toLowerCase().endsWith('.exe'));
        setLatestExe(exeAsset || null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestRelease();
  }, []);

  const handleDownloadClick = () => {
    if (latestExe) {
      logEventSafe('download_click', {
        file_name: latestExe.name,
        page_path: location.pathname
      });
    }
  };

  return (
    <PageShell>
      <motion.div className="space-y-10 py-4" initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4, delay: 0.1 }}>
        <PageBackLink />
        <div className="mx-auto max-w-4xl">
          <PagePanel className="space-y-8">
            <PageHero eyebrow="Start" title="Get Started for Free" description="Download the latest build and get Toolip into your workflow quickly." />

            {loading ? (
              <p className="text-center text-gray-300">Loading latest version...</p>
            ) : error ? (
              <p className="text-center text-red-400">{error}</p>
            ) : latestExe ? (
              <div className="text-center">
                <motion.a
                  href={latestExe.browser_download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleDownloadClick}
                  className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" />
                  </svg>
                  Download for Windows
                </motion.a>
              </div>
            ) : (
              <p className="text-center text-gray-300">No Windows installer (.exe) found in the latest release.</p>
            )}

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">How Toolip Works</h2>
              <p className="mb-2 text-gray-300">
                Toolip is designed to boost your productivity with a suite of professional-grade tools.
              </p>
              <p className="text-gray-300">
                Once installed, you can access a streamlined toolbelt that integrates task management,
                customization, and real-time system integrations to help you stay on top of your work.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">Why Choose Toolip?</h2>
              <p className="text-gray-300">
                Our mission is to help professionals work smarter, not harder. Toolip is constantly updated
                with new features and improvements based on your feedback, ensuring that you always have the best tools
                at your fingertips.
              </p>
            </section>
          </PagePanel>
        </div>
      </motion.div>
    </PageShell>
  );
};

export default GetStarted;
